'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModalProps } from '@mui/material';
import Link from '@mui/icons-material/Link';
import { UploadedMedia, UploadType } from '@dh/types';
import { BodyMedium } from '@dh/styles';
import { useDebounceEffect, useImageSizeCalculation } from '@dh/hooks';
import { isDomain, openInNewTab } from '@dh/utils';

import { ArrowButton, ArrowDirection } from '../buttons';
import {
    GalleryContent,
    GalleryImage,
    GalleryVideo,
    InfoTextContainer,
    ModalContainer,
    ModalContent,
    SourceTextButton
} from './styles';

interface Props extends Omit<ModalProps, 'children' | 'onClose'> {
    media: UploadedMedia<UploadType.Image | UploadType.Weblink>[];
    initialIndex: number;
    onClose: () => void;
}

const MediaSlider = ({ media, initialIndex, onClose, ...modalProps }: Props) => {
    const [index, setIndex] = useState(initialIndex);
    const infoTextRef = useRef<HTMLElement>(undefined);

    const arrowSize = 48;
    const { imageDimensions, containerRef, onImageLoad, setImageRatio } = useImageSizeCalculation({
        otherElementsHeight: infoTextRef?.current?.getBoundingClientRect().height ?? 0,
        otherElementsWidth: 2 * arrowSize
    });

    const selectedMedia = useMemo(() => media[index], [media, index]);
    const { type, name, url, description, source } = selectedMedia;

    const isImage = type === UploadType.Image;
    const hasInfo = !!source;

    const isFirstSlide = (i: number) => i === 0;
    const isLastSlide = (i: number) => i === media.length - 1;
    const nextSlide = () => setIndex((i) => (isLastSlide(i) ? i : i + 1));
    const prevSlide = () => setIndex((i) => (isFirstSlide(i) ? i : i - 1));

    useDebounceEffect(
        () => {
            const handleKeyPress = ({ code }: KeyboardEvent) => {
                if (code === 'ArrowRight') nextSlide();
                if (code === 'ArrowLeft') prevSlide();
            };

            addEventListener('keyup', handleKeyPress);
            return () => removeEventListener('keyup', handleKeyPress);
        },
        200,
        []
    );

    useEffect(() => {
        if (selectedMedia.type === UploadType.Weblink) {
            // set individual widget dimensions for some platforms
            if (isDomain(selectedMedia.url, 'mixcloud.com')) {
                setImageRatio(120 / 946);
            } else if (isDomain(selectedMedia.url, 'fb.watch')) {
                setImageRatio(556 / 1036);
                // set player default to fixed aspect ratio of 9/16
            } else setImageRatio(9 / 16);
        }
    }, [selectedMedia, setImageRatio]);

    function preventModalClosing(
        e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>
    ) {
        e.stopPropagation();
    }

    return (
        <ModalContainer onClose={onClose} {...modalProps}>
            <ModalContent ref={containerRef} onClick={onClose} stretch>
                <ArrowButton
                    disabled={isFirstSlide(index)}
                    direction={ArrowDirection.left}
                    onClick={(event) => {
                        preventModalClosing(event);
                        prevSlide();
                    }}
                />
                <GalleryContent onClick={preventModalClosing} style={{ ...imageDimensions }}>
                    {type === UploadType.Image ? (
                        <GalleryImage
                            fill
                            priority
                            hasInfo={hasInfo}
                            src={url}
                            alt={description ?? source ?? name ?? url}
                            onLoad={onImageLoad}
                        />
                    ) : (
                        <GalleryVideo url={url} {...imageDimensions} />
                    )}

                    {(hasInfo || !isImage) && (
                        <InfoTextContainer
                            ref={infoTextRef}
                            sx={{
                                width: imageDimensions?.width,
                                bottom: `-${infoTextRef?.current?.getBoundingClientRect().height ?? 0}px`
                            }}
                        >
                            {source && <BodyMedium sx={{ fontStyle: 'italic' }}>{source}</BodyMedium>}
                            {!isImage && (
                                <SourceTextButton
                                    icon={<Link />}
                                    label={url}
                                    onClick={() => openInNewTab(url)}
                                />
                            )}
                        </InfoTextContainer>
                    )}
                </GalleryContent>
                <ArrowButton
                    disabled={isLastSlide(index)}
                    direction={ArrowDirection.right}
                    onClick={(event) => {
                        preventModalClosing(event);
                        nextSlide();
                    }}
                />
            </ModalContent>
        </ModalContainer>
    );
};

export { MediaSlider };
